import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LeaveConfirmationModalComponent, NotificationService } from '@cinetixx/cinetixx-ui';
import { CreatorStateService } from './CreatorStateService';
import { CreatorSaverService } from './CreatorSaverService';
import { CreatorCacheService } from './CreatorCacheService';
import { CreatorCurrentStepService } from './CreatorCurrentStepService';

@Injectable({
  providedIn: 'root'
})
export class LeaveCreatorService {

  public constructor(
    private readonly _notificationService: NotificationService,
    private readonly _creatorStateSaverService: CreatorSaverService,
    private readonly _creatorCurrentStepService: CreatorCurrentStepService
  ) {
  }

  public canLeave(id?: number): Observable<boolean> {
    const canLeave$ = new Subject<boolean>();

    const closeModal = (): void => {
      canLeave$.next(true);
      canLeave$.complete();
      CreatorStateService.clearCurrentState();
      LeaveConfirmationModalComponent.instance.close();
    };

    const isSeatsPlanDirty = (): boolean => {
      let isDirty = false;

      if (!id) {
        const seatsPlanIds = [...Array.from(CreatorCacheService.seatsPlansCacheMap.keys()).filter(id => CreatorStateService.seatsPlan.id !== id), CreatorStateService.seatsPlan.id];

        for (const id of seatsPlanIds) {
          if (this._creatorCurrentStepService.isStepDirty(id)) {
            isDirty = true;
            break;
          }
        }
      } else {
        if (this._creatorCurrentStepService.isStepDirty(id)) {
          isDirty = true;
        }
      }

      return isDirty;
    };

    setTimeout(() => {
      const isDirty = isSeatsPlanDirty();
      
      if (isDirty) {
        LeaveConfirmationModalComponent.instance.resetState();

        LeaveConfirmationModalComponent.instance.onConfirm = () => {
          let apiCall$: Observable<unknown> = of(null);

          switch (CreatorStateService.currentStep) {
            case 'sector-plan':
              apiCall$ = this._creatorStateSaverService.saveSectorsPlan();
              break;
            case 'seats-plan':
              apiCall$ = this._creatorStateSaverService.saveSeatsPlan(false);
              break;
            case 'zones':
              apiCall$ = this._creatorStateSaverService.saveZoneConfiguration();
              break;
            case 'connections':
              apiCall$ = this._creatorStateSaverService.saveConnections();
              break;
            case 'seat-priority':
              apiCall$ = this._creatorStateSaverService.saveSeatPriority();
              break;
          }

          apiCall$
            .pipe(finalize(() => CreatorStateService.isLoading = false))
            .subscribe({
              next: () => {
                CreatorStateService.clearCurrentState();

                canLeave$.next(true);
                canLeave$.complete();

                LeaveConfirmationModalComponent.instance.isLoading = false;
                LeaveConfirmationModalComponent.instance.close();
              },
              error: () => {
                this._notificationService.pushError(
                  'Error'.trans('auditoriums/update-auditorium/notification/error.Title'),
                  'An error occurred while saving the sector'.trans('auditoriums/update-auditorium/notification/error.Message')
                );
                canLeave$.next(false);
                canLeave$.complete();
                LeaveConfirmationModalComponent.instance.isLoading = false;
                LeaveConfirmationModalComponent.instance.close();
              }
            });
        };

        LeaveConfirmationModalComponent.instance.onSecondaryButton = closeModal;
        LeaveConfirmationModalComponent.instance.closed.subscribe(() => {
          canLeave$.next(false);
          canLeave$.complete();
        });
        LeaveConfirmationModalComponent.instance.open();
      } else {
        CreatorStateService.clearCurrentState();
        canLeave$.next(true);
        canLeave$.complete();
      }
    });

    return canLeave$.asObservable();
  }
}
